/**
 * The words the page shows for a plant-model entity's kind and its lot operation, never their stored codes ("STATION",
 * "PASSTHROUGH"). The data keeps the codes; only what a person reads changes (the station workspaces plan, step 0:
 * docs/plans/2026-10-06_station-workspaces.md).
 *
 * "Station" is Arjun's word for the STATION kind (2026-10-07). A station as a group of equipment (the plan's stations,
 * at /app/stations) is a different thing and is named by its own name; on a piece of equipment, "Station" is its kind.
 * Pure.
 */
import type { EntityClass, LotOp } from "@/foundry-widgets/foundry-plant";

/** Each entity kind's word. */
export const ENTITY_CLASS_LABEL: Record<EntityClass, string> = {
  EQUIPMENT: "Equipment",
  STATION: "Station",
  INSTRUMENT: "Instrument",
  TRANSPORT: "Transport",
};

/** Each lot operation's word: what the entity does to the material lots that pass through it. */
export const LOT_OP_LABEL: Record<LotOp, string> = {
  MERGE: "Merge",
  TRANSFORM: "Transform",
  SPLIT: "Split",
  PASSTHROUGH: "Pass-through",
};

/** A code as words, for a kind or an operation a later model adds: "NEW_KIND" reads "New kind". */
export function codeWords(code: string): string {
  const words = code.replace(/_/g, " ").trim().toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : "";
}

/** An entity kind's word; a kind this page does not know reads as its code in words. */
export function entityClassLabel(cls: string): string {
  return (ENTITY_CLASS_LABEL as Record<string, string>)[cls] ?? codeWords(cls);
}

/** A lot operation's word; an operation this page does not know reads as its code in words. */
export function lotOpLabel(op: string): string {
  return (LOT_OP_LABEL as Record<string, string>)[op] ?? codeWords(op);
}
